// [MOBILE 2026-07-07] The phone presentation of a data table. On mobile, the
// shared DataTable delegates here instead of rendering a <table> (see DataTable.tsx).
//
// KEY IDEA — this is a PRESENTATION layer, not a re-implementation. It reuses the
// SAME `columns` (with their `render` fns), the SAME row objects, and the SAME
// `rowBgFn` the desktop table uses. So:
//   • a card's status colour comes from rowBgFn(row) — identical to the desktop
//     row tint (e.g. Payments mapped=green / unmapped=red), automatically.
//   • the tap-through detail renders EVERY column through its own `render`, so
//     inline-edit fields, "Edit Mapping"/"Create Deal" buttons, delete actions,
//     hover/`Tags` popovers etc. are the exact same cell code — no logic is
//     duplicated or changed. Whatever works in the table works here.
//
// A page can pass an optional `mobileCard` descriptor to control what leads the
// card face (title / value / meta / status pill); without it a heuristic picks a
// sensible identity + money column so every DataTable page gets usable cards for
// free. The detail always shows all columns regardless.
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { Column } from '../types';
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidSheet } from './Fluid'; // [FLUID-3 2026-10-12]

export type MobileTone = 'green' | 'red' | 'amber' | 'slate';

export interface MobileCardConfig {
  /** Column key whose raw value is the card title (the human identifier). */
  titleKey?: string;
  /** Column key whose rendered value is the prominent number (uses its column.render). */
  valueKey?: string;
  /** Column keys shown as small meta below the title (raw values). */
  metaKeys?: string[];
  /** Custom status pill, e.g. derived from a status flag not shown as its own column. */
  status?: (row: any) => { label: React.ReactNode; tone: MobileTone } | null | undefined;
}

interface Props {
  columns: Column[];
  data: any[];
  rowBgFn?: (row: any) => string | undefined;
  mobileCard?: MobileCardConfig;
  loading?: boolean;
}

const toneColor = (tone: MobileTone, theme: any): string => {
  const dark = theme.colorScheme === 'dark';
  switch (tone) {
    case 'green': return dark ? '#4acd8b' : theme.green600;
    case 'red': return theme.danger;
    case 'amber': return dark ? '#fbbf24' : '#b45309';
    default: return theme.muted;
  }
};

// Guess a status tone from free text so heuristic pills read the right colour
// without per-page config. Kept deliberately conservative.
const toneFromText = (v: any): MobileTone => {
  const s = String(v ?? '').toLowerCase();
  if (/retain|mapped|won|active|paid|recover|complete|success/.test(s)) return 'green';
  if (/risk|await|pending|partial|delay|hold|review/.test(s)) return 'amber';
  if (/cancel|need to map|unmapped|lost|fail|reject|refund|dispute|overdue/.test(s)) return 'red';
  return 'slate';
};

const VALUE_RE = /amount|total|net|value|bank|balance|inventory|retention|received|talktime|collection|revenue|gross|payment/i;
const SKIP_META_RE = /edit|delete|action|copy|tags|open|email|^\s*$/i;
const raw = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v));
const renderCell = (col: Column | undefined, row: any): React.ReactNode =>
  col ? (col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')) : null;

/* ── list ─────────────────────────────────────────────────────────────────── */
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 2px 2px 4px;
`;

const Card = styled.button<{ $bg?: string; $stripe?: MobileTone }>`
  position: relative;
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: block;
  background: ${(p) => p.$bg || p.theme.surface};
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  padding: 13px 40px 13px ${(p) => (p.$stripe ? '16px' : '14px')};
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1), border-color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  ${(p) => p.$stripe && `&::before{content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:${toneColor(p.$stripe, p.theme)};}`}

  &:active { transform: scale(0.995); }
  @media (hover: hover) {
    &:hover { box-shadow: 0 8px 22px rgba(0, 0, 0, 0.10); }
  }
`;

const CTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;
const CTitle = styled.span`
  font-size: 14.5px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.25;
  margin-right: auto;
  word-break: break-word;
  color: ${(p) => p.theme.text};
`;
const CValue = styled.span`
  font-size: 16.5px;
  font-weight: 800;
  letter-spacing: -0.02em;
  white-space: nowrap;
  color: ${(p) => p.theme.text};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;
const CMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  font-size: 12px;
  color: ${(p) => p.theme.muted};
`;
const Dot = styled.span`
  width: 3px; height: 3px; border-radius: 50%;
  background: currentColor; opacity: 0.5; flex-shrink: 0;
`;
const Pill = styled.span<{ $tone: MobileTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 8px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${(p) => toneColor(p.$tone, p.theme)};
  background: ${(p) => `color-mix(in srgb, ${toneColor(p.$tone, p.theme)} 16%, transparent)`};
  &::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
`;
const Chevron = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(p) => p.theme.muted};
  opacity: 0.5;
  pointer-events: none;
`;

const Empty = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  font-size: 14px;
`;

/* ── skeleton ─────────────────────────────────────────────────────────────── */
const shimmer = keyframes`0%{background-position:-320px 0}100%{background-position:320px 0}`;
const SkCard = styled.div`
  height: 74px;
  border-radius: 14px;
  border: 1px solid ${(p) => p.theme.line};
  background: linear-gradient(90deg,
    ${(p) => p.theme.surface} 0%,
    ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.04)')} 50%,
    ${(p) => p.theme.surface} 100%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* ── full-screen detail ───────────────────────────────────────────────────── */
// [AUTO-REFRESH 2026-08-03] See reloadGuard — blocks the auto-reload while the mobile record
// detail sheet is open.
const Overlay = styled.div.attrs<{ 'data-reload-block'?: string }>({ 'data-reload-block': '' })<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  /* [MOBILE 2026-07-07] Above the bottom dock (850) and all page content, but
     deliberately BELOW the app's modals (MappingModal/DealCreationModal 1000,
     ConfirmDialog 1100, AddPaymentModal 1200) so tapping Edit Mapping / Create
     Deal / Delete inside a record opens its modal ON TOP of this detail, then
     returns here on close. Toasts (3000) still win over everything. */
  z-index: 900;
  background: ${(p) => p.theme.bg};
  display: flex;
  flex-direction: column;
  /* [FLUID-3 2026-10-12, user] Still slides up from the bottom and back down, now on the app's spring (0.34 s
     in) and quicker out (0.22 s) — the side-panel recipe turned upright (components/Fluid.tsx fluidSheet). Was
     one 0.3 s keyframe played forwards, then in reverse. Open = translateX(0): a transform at rest, as the old
     keyframe's fill left one (translateY(0)), so the sheet stays the containing block it was. */
  ${(p) => fluidSheet(p.$open, 'translateY(100%)')}
`;
const DHead = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px 12px 4px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background: ${(p) => p.theme.surface};
  border-bottom: 1px solid ${(p) => p.theme.line};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;
const Back = styled.button`
  width: 42px; height: 42px; flex-shrink: 0;
  border: none; background: transparent; color: ${(p) => p.theme.text};
  display: grid; place-items: center; cursor: pointer; border-radius: 50%;
  &:active { background: ${(p) => p.theme.pill}; }
`;
const DTitle = styled.div`
  min-width: 0; margin-right: auto;
  b { display: block; font-size: 15.5px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${(p) => p.theme.text}; }
`;
const DBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px calc(28px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const DAmount = styled.div`
  text-align: center;
  padding: 8px 0 2px;
  .v { font-size: 32px; font-weight: 800; letter-spacing: -0.03em; color: ${(p) => p.theme.text}; font-variant-numeric: tabular-nums; }
`;
const DGroup = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  overflow: hidden;
`;
const DRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  &:last-child { border-bottom: none; }
`;
const RK = styled.span`
  font-size: 12.5px; font-weight: 600; color: ${(p) => p.theme.muted}; flex-shrink: 0;
`;
const RV = styled.span`
  font-size: 13.5px; font-weight: 700; text-align: right; color: ${(p) => p.theme.text};
  word-break: break-word; min-width: 0;
  display: flex; align-items: center; justify-content: flex-end; gap: 6px;
`;

const ChevronRightIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
);
const ChevronLeftIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
);

const MobileRecordList: React.FC<Props> = ({ columns, data, rowBgFn, mobileCard, loading }) => {
  const [openRow, setOpenRow] = useState<any | null>(null);
  const [closing, setClosing] = useState(false);

  const colByKey = useMemo(() => {
    const m: Record<string, Column> = {};
    columns.forEach((c) => { m[c.key] = c; });
    return m;
  }, [columns]);

  // Heuristic fallbacks when a page doesn't pass an explicit mobileCard.
  const pick = useMemo(() => {
    const byWord = (w: string) => columns.find((c) => new RegExp(w, 'i').test(c.key) || new RegExp(w, 'i').test(c.label));
    const titleCol =
      ['website', 'company', 'deal', 'url', 'account', 'person', 'particular', 'team', 'name', 'month'].map(byWord).find(Boolean) ||
      columns[0];
    const valueCol = columns.find((c) => c !== titleCol && VALUE_RE.test(`${c.key} ${c.label}`));
    const statusCol = columns.find((c) => /status/i.test(c.key) || /status/i.test(c.label));
    const used = new Set<Column>([titleCol, valueCol, statusCol].filter(Boolean) as Column[]);
    const metaCols = columns
      .filter((c) => !used.has(c) && !SKIP_META_RE.test(c.label) && !SKIP_META_RE.test(c.key))
      .slice(0, 3);
    return { titleCol, valueCol, statusCol, metaCols };
  }, [columns]);

  const titleOf = useCallback((row: any): React.ReactNode =>
    mobileCard?.titleKey ? raw(row[mobileCard.titleKey]) : raw(row[pick.titleCol?.key]),
    [mobileCard, pick]);

  const valueOf = useCallback((row: any): React.ReactNode =>
    mobileCard?.valueKey ? renderCell(colByKey[mobileCard.valueKey], row) : (pick.valueCol ? renderCell(pick.valueCol, row) : null),
    [mobileCard, colByKey, pick]);

  const statusOf = useCallback((row: any): { label: React.ReactNode; tone: MobileTone } | null => {
    if (mobileCard?.status) return mobileCard.status(row) || null;
    if (pick.statusCol) {
      const v = row[pick.statusCol.key];
      if (v === null || v === undefined || v === '') return null;
      return { label: String(v), tone: toneFromText(v) };
    }
    return null;
  }, [mobileCard, pick]);

  const metaOf = useCallback((row: any): { label: string; value: React.ReactNode }[] => {
    if (mobileCard?.metaKeys) {
      return mobileCard.metaKeys
        .map((k) => ({ label: colByKey[k]?.label ?? '', value: raw(row[k]) }))
        .filter((m) => m.value !== '—');
    }
    return pick.metaCols
      .map((c) => ({ label: c.label, value: raw(row[c.key]) }))
      .filter((m) => m.value !== '—');
  }, [mobileCard, colByKey, pick]);

  // Keep the open detail bound to the LIVE row (matched by id) so an edit made
  // inside it — e.g. saving a mapping — reflects immediately (pill flips to
  // Mapped, tint turns green) when the page refreshes its data, exactly like the
  // desktop row would. Falls back to the captured row when there's no id.
  const liveOpenRow = useMemo(() => {
    if (!openRow) return null;
    if (openRow.id == null) return openRow;
    return data.find((r) => r && r.id === openRow.id) || openRow;
  }, [openRow, data]);

  // [FLUID-3 2026-10-12] The sheet is up one frame after it mounts (so the slide has its from-state) until a
  // close starts; the close is held for the sheet's 0.22 s slide down (was 0.3 s).
  const sheetUp = usePresence(!!openRow && !closing, fluid.sheetOutMs).entered;
  const close = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => { setOpenRow(null); setClosing(false); }, fluid.sheetOutMs);
  }, []);

  // Esc + lock body scroll behind the detail.
  useEffect(() => {
    if (!openRow) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [openRow, close]);

  if (loading) {
    return <List>{Array.from({ length: 7 }).map((_, i) => <SkCard key={i} />)}</List>;
  }
  if (!data.length) {
    return <Empty>No records found.</Empty>;
  }

  return (
    <>
      <List>
        {data.map((row, i) => {
          const bg = rowBgFn?.(row);
          const status = statusOf(row);
          const meta = metaOf(row);
          const value = valueOf(row);
          const title = titleOf(row);
          return (
            <Card
              key={row?.id != null ? `${row.id}-${i}` : i}
              $bg={bg}
              $stripe={status?.tone}
              onClick={() => setOpenRow(row)}
              aria-label={`Open record ${typeof title === 'string' ? title : ''}`}
            >
              <CTop>
                <CTitle>{titleOf(row)}</CTitle>
                {value != null && value !== '—' && <CValue>{value}</CValue>}
              </CTop>
              {(status || meta.length > 0) && (
                <CMeta>
                  {status && <Pill $tone={status.tone}>{status.label}</Pill>}
                  {meta.map((m, mi) => (
                    <React.Fragment key={mi}>
                      {(status || mi > 0) && <Dot />}
                      <span>{m.value}</span>
                    </React.Fragment>
                  ))}
                </CMeta>
              )}
              <Chevron>{ChevronRightIcon}</Chevron>
            </Card>
          );
        })}
      </List>

      {liveOpenRow && createPortal(
        (() => {
          const dStatus = statusOf(liveOpenRow);
          const dValue = valueOf(liveOpenRow);
          // [FLUID-3 2026-10-12] Inert while it slides away: nothing in a closing sheet can be used.
          return (
            <Overlay $open={sheetUp} inert={closing} role="dialog" aria-modal="true">
              <DHead>
                <Back onClick={close} aria-label="Back">{ChevronLeftIcon}</Back>
                <DTitle><b>{titleOf(liveOpenRow)}</b></DTitle>
                {dStatus && <Pill $tone={dStatus.tone}>{dStatus.label}</Pill>}
              </DHead>
              <DBody>
                {dValue != null && dValue !== '—' && (
                  <DAmount><div className="v">{dValue}</div></DAmount>
                )}
                <DGroup>
                  {columns.map((col) => (
                    <DRow key={col.key}>
                      <RK>{col.label}</RK>
                      <RV>{renderCell(col, liveOpenRow)}</RV>
                    </DRow>
                  ))}
                </DGroup>
              </DBody>
            </Overlay>
          );
        })(),
        document.body,
      )}
    </>
  );
};

export default MobileRecordList;
